
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FilterIcon, Package, Search } from "lucide-react";
import React, { useEffect, useState } from 'react';
import { AdminPagination, usePagination } from '@/components/ui/admin-pagination';

interface OrderItem {
  id: string;
  quantity: number;
  points_price: number;
  products: { name: string; image: string; };
}

interface Order {
  id: string;
  user_id: string;
  created_at: string;
  status: string;
  points_used: number;
  tracking_number?: string | null;
  admin_notes?: string | null;
  items?: OrderItem[];
  user_profile?: {
    email: string;
    username: string;
    full_name?: string;
  };
  stores?: {
    name: string;
    location?: string;
  };
}

const orderStatuses = [
  { value: 'pending', label: 'Pendente', color: 'bg-yellow-500' },
  { value: 'confirmed', label: 'Confirmado', color: 'bg-indigo-500' },
  { value: 'processing', label: 'Processando', color: 'bg-blue-500' },
  { value: 'shipped', label: 'Enviado', color: 'bg-purple-500' },
  { value: 'delivered', label: 'Entregue', color: 'bg-green-500' },
  { value: 'cancelled', label: 'Cancelado', color: 'bg-red-500' }
];

const statusColors = Object.fromEntries(orderStatuses.map(s => [s.value, s.color]));
const statusLabels = Object.fromEntries(orderStatuses.map(s => [s.value, s.label]));

const OrdersManager: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  // Removed unused row dropdown toggle
  const [statusToUpdate, setStatusToUpdate] = useState<{ orderId: string; newStatus: string } | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [observation, setObservation] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const { pagination, setPage, setLimit, setTotal } = usePagination(20);

  useEffect(() => { fetchOrdersAdmin(); }, [pagination.page, pagination.limit, filterStatus, searchQuery]);

  // Load order items on demand when opening the details modal
  useEffect(() => {
    const loadItems = async () => {
      if (!selectedOrder) return;
      // If items already loaded, skip
      if (selectedOrder.items && selectedOrder.items.length > 0) return;
      try {
        const { data, error } = await supabase
          .from('order_items')
          .select('id, quantity, points_price, products(name, image)')
          .eq('order_id', selectedOrder.id);
        if (error) throw error;
        const items = (data || []).map((row: any) => ({
          id: row.id,
          quantity: row.quantity,
          points_price: row.points_price,
          products: { name: row.products?.name || 'Produto', image: row.products?.image || '' }
        }));
        setSelectedOrder({ ...selectedOrder, items });
      } catch (e) {
        console.warn('Falha ao carregar itens do pedido:', e);
      }
    };
    loadItems();
  }, [selectedOrder]);

  const fetchOrdersAdmin = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('admin-manage-orders', {
        body: {
          action: 'list',
          orderData: {
            page: pagination.page,
            limit: pagination.limit,
            status: filterStatus !== 'all' ? filterStatus : undefined,
            search: searchQuery.trim() || undefined
          }
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao caregar encomendas');

      // Transform the orders from Edge Function response
      const formattedOrders = (data.orders || []).map((order: any) => ({
        id: order.id,
        user_id: order.user_id,
        created_at: order.created_at,
        status: order.status,
        points_used: order.points_used || 0,
        items: order.order_items || [],
        user_profile: order.user_profile,
        stores: order.stores
      }));

      setOrders(formattedOrders);

      // Update pagination with total count
      if (data.pagination) {
        setTotal(data.pagination.total || 0);
      }
    } catch (error: any) {
      // Prevent technical information leakage - provide user-friendly messages
      let userMessage = 'Não foi possível caregar as encomendas. Tente novamente.';

      // Log technical details only in console for debugging
      console.error('OrdersManager.fetchOrdersAdmin error:', error);

      if (error.message?.includes('network') || error.message?.includes('fetch')) {
        userMessage = 'Erro de conexão. Verifique a sua ligação à internet.';
      } else if (error.message?.includes('Unauthorized') || error.message?.includes('401')) {
        userMessage = 'Sessão expirada. Faça login novamente.';
      } else if (error.message?.includes('timeout')) {
        userMessage = 'A operação demorou muito tempo. Tente novamente.';
      }

      toast({
        title: 'Erro ao caregar encomendas',
        description: userMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => format(new Date(date), "dd 'de' MMMM 'de' yyyy, HH:mm", { locale: ptBR });

  // Abre modal para alterar status
  const openStatusModal = (orderId: string, currentStatus: string) => {
    setStatusToUpdate({ orderId, newStatus: currentStatus });
    setTrackingNumber('');
    setObservation('');
  };

  // Atualiza status do pedido via Edge Function
  const updateOrderStatus = async () => {
    if (!statusToUpdate) return;
    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-orders', {
        body: {
          action: 'update_status',
          orderId: statusToUpdate.orderId,
          orderData: {
            status: statusToUpdate.newStatus,
            tracking_number: trackingNumber || null,
            admin_notes: observation || null
          }
        }
      });

      if (error) {
        console.error('Erro ao actualizar estado:', error);
        toast({ title: 'Erro ao actualizar estado', description: 'Não foi possível actualizar o estado. Tente novamente.', variant: 'destructive' });
        return;
      }

      if (!data?.success) {
        toast({ title: 'Erro ao actualizar estado', description: 'Não foi possível actualizar o estado. Tente novamente.', variant: 'destructive' });
        return;
      }

      toast({ title: 'Estado actualizado', description: `Pedido actualizado para ${statusLabels[statusToUpdate.newStatus]}` });
      setOrders(orders.map(o => o.id === statusToUpdate.orderId ? { ...o, status: statusToUpdate.newStatus } : o));
      if (selectedOrder?.id === statusToUpdate.orderId) setSelectedOrder({ ...selectedOrder, status: statusToUpdate.newStatus });

      setStatusToUpdate(null);
      setTrackingNumber('');
      setObservation('');
    } catch (error: any) {
      console.error('Erro inesperado:', error);
      toast({ title: 'Erro ao actualizar estado', description: 'Ocorreu um erro inesperado. Tente novamente.', variant: 'destructive' });
    }
  };

  // Filtra pedidos por status
  const filteredOrders = filterStatus === 'all' ? orders : orders.filter(o => o.status === filterStatus);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="flex gap-2 items-center">
          <FilterIcon size={16} />
          <span className="text-sm text-muted-foreground">Filtrar por status:</span>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px] bg-brand-gray/50">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {orderStatuses.map(status => (
                <SelectItem key={status.value} value={status.value}>
                  <div className="flex items-center">
                    <div className={`h-2 w-2 rounded-full mr-2 ${status.color}`} />
                    {status.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={fetchOrdersAdmin} disabled={loading} className="bg-transparent border-gray-600 hover:bg-gray-800">
          {loading ? 'A caregar...' : 'Actualizar encomendas'}
        </Button>
      </div>

      <Card className="bg-brand-gray border-none text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package size={20} />
            Gestão de Encomendas
          </CardTitle>
          <CardDescription className="text-gray-400">Gira todas as encomendas e actualize os seus estados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red"></div>
            </div>
          ) : filteredOrders.length > 0 ? (
            <div className="rounded-md border border-gray-700">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-400">ID</TableHead>
                    <TableHead className="text-gray-400">Cliente</TableHead>
                    <TableHead className="text-gray-400">Data</TableHead>
                    <TableHead className="text-gray-400">Pontos</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map(order => (
                    <TableRow key={order.id} className="border-gray-700 hover:bg-brand-darker/50 cursor-pointer" onClick={() => setSelectedOrder(order)}>
                      <TableCell>{order.id.slice(0, 8)}</TableCell>
                      <TableCell>{order.user_profile?.full_name || order.user_profile?.username || order.user_profile?.email || 'Cliente desconhecido'}</TableCell>
                      <TableCell>{formatDate(order.created_at)}</TableCell>
                      <TableCell>{order.points_used} pontos</TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[order.status]} text-white`}>
                          {statusLabels[order.status] || order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={e => { e.stopPropagation(); openStatusModal(order.id, order.status); }} className="border-gray-700 hover:bg-gray-800">
                          Alterar Status
                        </Button>
                        <Button variant="outline" size="sm" onClick={e => { e.stopPropagation(); setSelectedOrder(order); }} className="border-gray-700 hover:bg-gray-800">
                          <Search size={16} className="mr-2" />
                          Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">Nenhum pedido encontrado</div>
          )}

          {/* Pagination */}
          {!loading && filteredOrders.length > 0 && (
            <div className="mt-4">
              <AdminPagination
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal para alterar status */}
      <Dialog open={!!statusToUpdate} onOpenChange={() => setStatusToUpdate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar alteração de status</DialogTitle>
            <DialogDescription>
              Selecione o novo estado do pedido e, se aplicável, adicione o número de rastreio e observações.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Label>Status</Label>
            <Select
              value={statusToUpdate?.newStatus || ''}
              onValueChange={(value) => setStatusToUpdate(prev => prev ? { ...prev, newStatus: value } : null)}
            >
              <SelectTrigger className="w-full bg-brand-gray/50">
                <SelectValue>{statusLabels[statusToUpdate?.newStatus || ''] || 'Seleccione um estado'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {orderStatuses.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    <div className="flex items-center">
                      <div className={`h-2 w-2 rounded-full mr-2 ${status.color}`} />
                      {status.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {statusToUpdate?.newStatus === 'shipped' && (
              <div>
                <Label htmlFor="tracking">Número de rastreio</Label>
                <Input id="tracking" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} />
              </div>
            )}

            <div>
              <Label htmlFor="obs">Observações</Label>
              <Input id="obs" value={observation} onChange={e => setObservation(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="ghost" onClick={() => setStatusToUpdate(null)}>Cancelar</Button>
            <Button onClick={updateOrderStatus}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de detalhes do pedido */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-3xl">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle>Detalhes do Pedido #{selectedOrder.id.slice(0, 8)}</DialogTitle>
                <DialogDescription>
                  Informações completas da encomenda seleccionada.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-3">Detalhes do Pedido</h3>
                    <div className="space-y-2">
                      <p><span className="text-gray-400">ID:</span> {selectedOrder.id}</p>
                      <p><span className="text-gray-400">Data:</span> {formatDate(selectedOrder.created_at)}</p>
                      <p><span className="text-gray-400">Cliente:</span> {selectedOrder.user_profile?.full_name || selectedOrder.user_profile?.username || selectedOrder.user_profile?.email || 'Cliente desconhecido'}</p>
                      <p><span className="text-gray-400">Email:</span> {selectedOrder.user_profile?.email || 'N/A'}</p>
                      <p><span className="text-gray-400">Pontos Utilizados:</span> {selectedOrder.points_used}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-3">Status do Pedido</h3>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <span className="text-gray-400 mr-2">Status Atual:</span>
                        <Badge className={`${statusColors[selectedOrder.status] || 'bg-gray-500'}`}>
                          {statusLabels[selectedOrder.status] || selectedOrder.status}
                        </Badge>
                      </div>
                      {selectedOrder.stores && (
                        <div>
                          <span className="text-gray-400">Loja de Entrega:</span>
                          <p className="font-medium mt-1">{selectedOrder.stores.name}</p>
                          {selectedOrder.stores.location && (
                            <p className="text-sm text-gray-400 mt-1">{selectedOrder.stores.location}</p>
                          )}
                        </div>
                      )}
                      {selectedOrder.status === 'shipped' && selectedOrder.tracking_number && (
                        <div>
                          <span className="text-gray-400">Número de Rastreio:</span>
                          <p className="font-mono text-sm mt-1">{selectedOrder.tracking_number}</p>
                        </div>
                      )}
                      {selectedOrder.admin_notes && (
                        <div>
                          <span className="text-gray-400">Observações:</span>
                          <p className="text-sm mt-1">{selectedOrder.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-3">Itens do Pedido</h3>
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    <div className="rounded-md border border-gray-700">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-700 hover:bg-transparent">
                            <TableHead className="text-gray-400">Produto</TableHead>
                            <TableHead className="text-gray-400">Quantidade</TableHead>
                            <TableHead className="text-gray-400">Preço (pontos)</TableHead>
                            <TableHead className="text-gray-400">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedOrder.items.map(item => (
                            <TableRow key={item.id} className="border-gray-700">
                              <TableCell>{item.products.name}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>{item.points_price}</TableCell>
                              <TableCell>{item.quantity * item.points_price}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-gray-400">Carregando itens do pedido...</p>
                  )}
                </div>
              </div>
              <DialogFooter className="pt-4">
                <Button onClick={() => setSelectedOrder(null)} variant="outline" className="bg-transparent border-gray-600 hover:bg-gray-800">
                  Fechar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManager;
